import React, { useState } from 'react';
import { Project } from '../types/project';
import Button from './Button';

interface DeleteProjectModalProps {
    project: Project;
    taskCount: number;
    availableProjects: Project[];
    onConfirm: (cascade: boolean, reassignTo?: string) => void;
    onCancel: () => void;
}

const DeleteProjectModal: React.FC<DeleteProjectModalProps> = ({
    project,
    taskCount,
    availableProjects,
    onConfirm,
    onCancel,
}) => {
    const [deleteOption, setDeleteOption] = useState<'cancel' | 'cascade' | 'reassign'>('cancel');
    const [selectedProjectId, setSelectedProjectId] = useState<string | undefined>(undefined);

    const handleConfirm = () => {
        if (deleteOption === 'cascade') {
            onConfirm(true);
        } else if (deleteOption === 'reassign' && selectedProjectId) {
            onConfirm(false, selectedProjectId);
        }
    };

    const canConfirm =
        deleteOption === 'cascade' ||
        (deleteOption === 'reassign' && selectedProjectId !== undefined);

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                <div style={styles.header}>
                    <h2 style={styles.title}>Delete Project: {project.name}</h2>
                </div>

                {taskCount > 0 ? (
                    <div style={styles.body}>
                        <div style={styles.warning}>
                            <span style={styles.warningIcon}>⚠️</span>
                            <p style={styles.warningText}>
                                This project has <strong>{taskCount}</strong> associated task{taskCount !== 1 ? 's' : ''}.
                            </p>
                        </div>

                        <p style={styles.question}>What would you like to do with the tasks?</p>

                        <div style={styles.options}>
                            <label style={styles.option}>
                                <input
                                    type="radio"
                                    name="deleteOption"
                                    value="cascade"
                                    checked={deleteOption === 'cascade'}
                                    onChange={() => setDeleteOption('cascade')}
                                    style={styles.radio}
                                />
                                <div style={styles.optionContent}>
                                    <div style={styles.optionTitle}>Delete all tasks</div>
                                    <div style={styles.optionDescription}>
                                        Permanently delete the project and all {taskCount} associated task{taskCount !== 1 ? 's' : ''}
                                    </div>
                                </div>
                            </label>

                            <label style={styles.option}>
                                <input
                                    type="radio"
                                    name="deleteOption"
                                    value="reassign"
                                    checked={deleteOption === 'reassign'}
                                    onChange={() => setDeleteOption('reassign')}
                                    style={styles.radio}
                                />
                                <div style={styles.optionContent}>
                                    <div style={styles.optionTitle}>Reassign tasks to another project</div>
                                    <div style={styles.optionDescription}>
                                        Move all tasks to a different project before deleting
                                    </div>
                                    {deleteOption === 'reassign' && (
                                        <select
                                            value={selectedProjectId || ''}
                                            onChange={(e) => setSelectedProjectId(e.target.value)}
                                            className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                                            onClick={(e) => e.stopPropagation()}
                                            style={{ marginTop: '12px' }}
                                        >
                                            <option value="">Select a project...</option>
                                            {availableProjects
                                                .filter(p => p.id !== project.id)
                                                .map(p => (
                                                    <option key={p.id} value={p.id}>
                                                        {p.name}
                                                    </option>
                                                ))
                                            }
                                        </select>
                                    )}
                                </div>
                            </label>

                            <label style={styles.option}>
                                <input
                                    type="radio"
                                    name="deleteOption"
                                    value="cancel"
                                    checked={deleteOption === 'cancel'}
                                    onChange={() => setDeleteOption('cancel')}
                                    style={styles.radio}
                                />
                                <div style={styles.optionContent}>
                                    <div style={styles.optionTitle}>Cancel deletion</div>
                                    <div style={styles.optionDescription}>
                                        Keep the project and its tasks
                                    </div>
                                </div>
                            </label>
                        </div>
                    </div>
                ) : (
                    <div style={styles.body}>
                        <p>Are you sure you want to delete this project?</p>
                        <p style={styles.noTasksNote}>This project has no associated tasks.</p>
                    </div>
                )}

                <div style={styles.footer}>
                    <Button onClick={onCancel} variant="outline">
                        Cancel
                    </Button>
                    {taskCount > 0 ? (
                        <Button
                            onClick={handleConfirm}
                            disabled={!canConfirm}
                            variant="danger"
                        >
                            {deleteOption === 'cascade' ? 'Delete Project & Tasks' :
                                deleteOption === 'reassign' ? 'Reassign & Delete Project' :
                                    'Select an option'}
                        </Button>
                    ) : (
                        <Button onClick={() => onConfirm(false)} variant="danger">
                            Delete Project
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

const styles = {
    overlay: {
        position: 'fixed' as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
    },
    modal: {
        backgroundColor: 'white',
        borderRadius: '8px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    },
    header: {
        padding: '20px',
        borderBottom: '1px solid #ddd',
        backgroundColor: '#f9f9f9',
    },
    title: {
        margin: 0,
        color: '#333',
        fontSize: '20px',
    },
    body: {
        padding: '24px',
    },
    warning: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '16px',
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '6px',
        marginBottom: '20px',
    },
    warningIcon: {
        fontSize: '24px',
    },
    warningText: {
        margin: 0,
        color: '#856404',
        fontSize: '14px',
    },
    question: {
        fontSize: '16px',
        fontWeight: 'bold' as const,
        marginBottom: '16px',
        color: '#333',
    },
    options: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '12px',
    },
    option: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '16px',
        border: '2px solid #ddd',
        borderRadius: '6px',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    radio: {
        marginTop: '4px',
        cursor: 'pointer',
    },
    optionContent: {
        flex: 1,
    },
    optionTitle: {
        fontWeight: 'bold' as const,
        fontSize: '15px',
        marginBottom: '4px',
        color: '#333',
    },
    optionDescription: {
        fontSize: '13px',
        color: '#666',
    },
    noTasksNote: {
        fontSize: '14px',
        color: '#666',
        fontStyle: 'italic' as const,
    },
    footer: {
        padding: '16px 20px',
        borderTop: '1px solid #ddd',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        backgroundColor: '#f9f9f9',
    },
};

export default DeleteProjectModal;
